import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { observer } from 'mobx-react';
import { EventOutlined } from '@mui/icons-material';

import { CoachTaskPriority } from '../../../../Model/CoachTask';
import { activityStatus } from '../../../../Model/CoachActivity';
import { DateFormat, toJsDate } from '../../../../../utils/date';
import AttachmentSlider from '../../../../components/AttachmentSlider';
import {
  TaskContainer,
  TaskTitle,
  TaskTitleRow,
  PriorityChip,
  TaskMeta,
  TaskInstructions,
  SourcePreviewCard,
  SourcePreviewLabel,
  SourcePreviewText,
} from './styles';
import texts from './texts.json';

const PRIORITY_CHIP_COLOR = {
  [CoachTaskPriority.URGENT]: 'error',
  [CoachTaskPriority.HIGH]: 'warning',
  [CoachTaskPriority.MEDIUM]: 'info',
  [CoachTaskPriority.LOW]: 'default',
};

const CoachTaskContent = ({ activity }) => {
  const {
    taskTitle, priority, deadline, status, instructions, attachments = [], messagePreview, highlight,
  } = activity;

  const deadlineDate = toJsDate(deadline);
  const hasValidDeadline = deadlineDate && moment(deadlineDate).isValid();
  const isOverdue = status === activityStatus.IN_PROGRESS && hasValidDeadline && deadlineDate < new Date();
  const deadlineLabel = hasValidDeadline
    && `${isOverdue ? 'Overdue · ' : 'Due '}${moment(deadlineDate).format(DateFormat.DATE_MONTH_YEAR_TIME_12H_FORMAT)}`;

  return (
    <TaskContainer>
      {(taskTitle || priority || hasValidDeadline) && (
        <TaskTitleRow>
          {taskTitle && <TaskTitle>{taskTitle}</TaskTitle>}
          {priority && (
            <PriorityChip
              label={priority}
              color={PRIORITY_CHIP_COLOR[priority] || 'default'}
              size="small"
              variant="outlined"
            />
          )}
          {hasValidDeadline && (
            <TaskMeta $isOverdue={isOverdue}>
              <EventOutlined />
              {deadlineLabel}
            </TaskMeta>
          )}
        </TaskTitleRow>
      )}
      {instructions && <TaskInstructions>{instructions}</TaskInstructions>}
      {messagePreview && (
        <SourcePreviewCard>
          <SourcePreviewLabel>{texts.messagePreviewLabel}</SourcePreviewLabel>
          {!!messagePreview.attachments?.length && (
            <AttachmentSlider attachments={messagePreview.attachments} maxHeight="140px" />
          )}
          {messagePreview.text && <SourcePreviewText>{messagePreview.text}</SourcePreviewText>}
        </SourcePreviewCard>
      )}
      {highlight && (
        <SourcePreviewCard>
          <SourcePreviewLabel>{texts.highlightLabel}</SourcePreviewLabel>
          {!!highlight.attachments?.length && (
            <AttachmentSlider attachments={highlight.attachments} maxHeight="220px" />
          )}
          {highlight.text && <SourcePreviewText>{highlight.text}</SourcePreviewText>}
        </SourcePreviewCard>
      )}
      {!!attachments.length && (
        <AttachmentSlider attachments={attachments} maxHeight="220px" />
      )}
    </TaskContainer>
  );
};

CoachTaskContent.propTypes = {
  activity: PropTypes.object.isRequired,
};

export default observer(CoachTaskContent);
